import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardBookings() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    loadBookings();
  }, [navigate]);

  const loadBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle prenotazioni');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/bookings/${bookingId}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Stato aggiornato');
      loadBookings();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const downloadPDF = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/bookings/${bookingId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prenotazione_${bookingId.substring(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore nella generazione del PDF');
    }
  };

  const sendWhatsApp = async (bookingId, target) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings/${bookingId}/whatsapp?target=${target}`);
      window.open(response.data.whatsapp_link, '_blank');
    } catch (error) {
      toast.error('Errore nella generazione del link WhatsApp');
    }
  };

  const getStatusColor = (status) => {
    const colors = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-green-100 text-green-800', completed: 'bg-blue-100 text-blue-800', cancelled: 'bg-red-100 text-red-800' };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const dateLocale = lang === 'en' ? enUS : it;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-bookings">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              {lang === 'en' ? 'Booking Management' : 'Gestione Prenotazioni'}
            </h1>
            <p className="text-gray-600">{lang === 'en' ? 'View and manage all bookings' : 'Visualizza e gestisci tutte le prenotazioni'}</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" data-testid="bookings-loading" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="bookings-empty">
              {lang === 'en' ? 'No bookings found' : 'Nessuna prenotazione trovata'}
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking, idx) => (
                <div key={booking.id} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-testid={`booking-row-${idx}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>{booking.customer_name}</h3>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>{booking.status}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{booking.customer_phone} {booking.customer_email && `| ${booking.customer_email}`}</p>
                      <p className="text-sm text-gray-700 font-medium">{booking.service_names?.join(', ')}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {format(new Date(booking.date), 'dd MMM yyyy', { locale: dateLocale })} - {booking.time_slot}
                      </p>
                      {booking.notes && <p className="text-sm text-gray-500 mt-1 italic">Note: {booking.notes}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Select value={booking.status} onValueChange={(v) => updateStatus(booking.id, v)}>
                        <SelectTrigger className="w-36" data-testid={`status-select-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" onClick={() => downloadPDF(booking.id)} data-testid={`pdf-btn-${idx}`}>
                        <FileText className="w-4 h-4 mr-1" /> PDF
                      </Button>
                      <Button size="sm" className="bg-[#25D366] hover:bg-[#128C7E] text-white" onClick={() => sendWhatsApp(booking.id, 'customer')} data-testid={`wa-customer-btn-${idx}`}>
                        <MessageCircle className="w-4 h-4 mr-1" /> {lang === 'en' ? 'Client' : 'Cliente'}
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white" onClick={() => sendWhatsApp(booking.id, 'owner')} data-testid={`wa-owner-btn-${idx}`}>
                        <MessageCircle className="w-4 h-4 mr-1" /> {lang === 'en' ? 'Owner' : 'Titolare'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
